import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ContentChild,
  ViewEncapsulation
} from '@angular/core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FilterField } from './filter-field.model';
import { TuiDay } from '@taiga-ui/cdk';

/**
 * Componente genérico para listar elementos con soporte de filtrado dinámico,
 * paginación y plantillas personalizadas para items, encabezado, pie y acciones.
 *
 * Permite definir campos de filtro dinámicamente, construir formularios reactivos
 * y aplicar filtros a la lista de items.
 *
 * Usa `FormBuilder` para construir el formulario de filtros y RxJS para manejar
 * cambios con debounce.
 *
 * También soporta plantillas ng-template personalizadas para mostrar los items,
 * encabezado, pie y acciones.
 */
@Component({
  selector: 'app-generic-list',
  templateUrl: './generic-list.component.html',
  styles: [`
    .filters {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-bottom: 1rem;
    }

    .results > div {
      /*border-bottom: 1px solid #ccc;*/
      padding: 0.5rem 0;
    }

    .filter-date-range {
      margin-bottom: 1rem;
      tui-input-date-range {
        width: 100%;
      }
    }

    .filter-actions {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      margin-top: 1rem;

      button {
        background: linear-gradient(180deg, var(--color-light-red) 0%, var(--color-dark-red) 100%);
        color: white;
        border: none;
        padding: 6px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 500;
        transition: background-color 0.3s ease;
      }
      button.clean{
        background: #fff;
        color: var(--color-light-red);
        border: 1px solid var(--color-light-red);
      }
    }

    .filters-form {
      display: flex;
      flex-direction: column;
      padding: 8px 16px;
      /*gap: 1rem;*/
    }

    .filters-row {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
    }

    .filter-field {
      flex: 1 1 250px; /* crece, encoge, ancho mínimo base */
      min-width: 200px;
    }

    .checkbox-wrapper {
      display: flex;
      align-items: center;
      padding-left: 1rem;
    }

    .filter-label {
      font-weight: 500;
      margin-bottom: 4px;
      display: inline-block;
    }

    .pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      margin-top: 1.5rem;

      button {
        background: linear-gradient(180deg, var(--color-light-red) 0%, var(--color-dark-red) 100%);
        color: white;
        border: none;
        padding: 6px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 500;
        transition: background-color 0.3s ease;
      }

      button:disabled {
        background: #ccc;
        cursor: not-allowed;
      }

      span {
        font-weight: 500;
        font-size: 14px;
      }
    }

    .filter-toggle {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 0.5rem;

      button {
        background-color: #f0f0f0;
        color: #333;
        border: 1px solid #ccc;
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
      }
    }

    .range-wrapper {
      display: flex;
      flex-direction: column;

      .range-labels {
        display: flex;
        justify-content: space-between;
        font-size: 0.8rem;
        margin-bottom: 0.25rem;
        color: gray;
      }
    }

    .custom-range-wrapper {
      position: relative;
      padding-top: 2rem;

      .thumb-value {
        position: absolute;
        top: 0;
        transform: translateX(-50%);
        background-color: var(--color-light-red);
        color: white;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 0.75rem;
        white-space: nowrap;
        pointer-events: none;
        transition: left 0.05s linear;
      }
    }

    tui-range {
      --tui-primary: var(--color-light-red); // color del thumb y parte activa del track
      --tui-base-03: #e0e0e0; // color del track inactivo
    }

    tui-slider-thumb {
      background-color: var(--color-light-red) !important;
    }

  `],
  encapsulation: ViewEncapsulation.None
})
export class GenericListComponent implements OnChanges, OnInit {



  /**
   * Lista de elementos a mostrar y filtrar.
   */
  @Input() items: any[] = [];

  /**
   * Definición de campos para filtrar la lista.
   * Cada campo indica tipo, clave y otras opciones.
   */
  @Input() filterFields: FilterField[] = [];

  /**
   * Indica si se deben mostrar los filtros.
   */
  @Input() showFilters = true;

  @Input() filtersCollapsedByDefault = false;


  /**
   * Indica si se puede descargar la lista.
   */
  @Input() allowDownload = true;

  /**
   * Prefijo opcional para las claves de los filtros,
   * útil para evitar colisiones si hay varios formularios en la vista.
   */
  @Input() filterKeyPrefix: string = '';

  /**
   * Evento emitido cuando se hace clic en un item.
   */
  @Output() itemClick = new EventEmitter<any>();

  /**
   * Evento emitido cuando cambian los filtros aplicados.
   */
  @Output() filtersChanged = new EventEmitter<any>();

  /**
   * Evento emitido cuando cambia la página de paginación.
   */
  @Output() pageChanged = new EventEmitter<number>();

  /**
   * Evento emitido con el número total de items filtrados.
   */
  @Output() filteredCount = new EventEmitter<number>();

  /**
   * Evento emitido con los items filtrados.
   */
  @Output() filteredItemsChange = new EventEmitter<any[]>();

  /**
   * Evento emitido con los valores de filtros activos.
   */
  @Output() activeFiltersChange = new EventEmitter<any>();

  /**
   * Plantilla personalizada para mostrar un item.
   */
  @ContentChild('itemTemplate', { static: false }) itemTemplateRef!: TemplateRef<any>;

  /**
   * Plantilla personalizada para mostrar el encabezado.
   */
  @ContentChild('headerTemplate', { static: false }) headerTemplateRef!: TemplateRef<any>;

  /**
   * Plantilla personalizada para mostrar el encabezado.
   */
  @ContentChild('tableHeaderTemplate', { static: false }) tableHeaderTemplateRef!: TemplateRef<any>;

  /**
   * Plantilla personalizada para mostrar el pie.
   */
  @ContentChild('footerTemplate', { static: false }) footerTemplateRef!: TemplateRef<any>;

  /**
   * Plantilla personalizada para mostrar acciones adicionales.
   */
  @ContentChild('actionsTemplate', { static: false }) actionsTemplateRef!: TemplateRef<any>;

  /**
   * Lista de elementos filtrados tras aplicar filtros.
   */
  filteredItems: any[] = [];

  /**
   * Lista paginada de elementos para la página actual.
   */
  paginatedItems: any[] = [];

  /**
   * Formulario reactivo que contiene los controles para los filtros.
   */
  filterForm: FormGroup = this.fb.group({});

  /**
   * Tamaño de página para paginación.
   */
  @Input() pageSize: number = 5;

  /**
   * Página actual visible.
   */
  currentPage = 1;

  /**
   * Fecha mínima permitida para filtros tipo rango de fecha.
   */
  minDate = new TuiDay(2020, 0, 1);

  /**
   * Fecha máxima permitida para filtros tipo rango de fecha (hoy).
   */
  maxDate = TuiDay.currentLocal();

  /**
   * Últimos valores emitidos de filtros, para evitar emitir cambios repetidos.
   */
  lastEmittedFilters: any = null;

  /**
   * Flag para controlar la visibilidad del bloque formulario.
   */
    @Input() isFiltersCollapsed = false;

  collapseKey = 'filtersCollapsed_default';


  leftThumbPosition = 0;
  rightThumbPosition = 100;
  rangeValue = [0, 100];
  field: any;

  private defaultFilterValues: any = {};

  constructor(private fb: FormBuilder) { }

  /**
   * Cambia la visibilidad del formulario.
   */
  toggleFilters(): void {
    console.log(this.collapseKey)
    this.isFiltersCollapsed = !this.isFiltersCollapsed;
    localStorage.setItem(this.collapseKey, String(this.isFiltersCollapsed));
  }

  ngOnInit(): void {
    const stored = localStorage.getItem(this.collapseKey);
    //this.isFiltersCollapsed = stored === 'true';
  }

  /**
   * Detecta cambios en las propiedades `filterFields` e `items`.
   * Reconstruye el formulario si cambian los campos y aplica filtros
   * si hay datos y campos disponibles.
   *
   * @param changes Objeto con los cambios detectados
   */
  ngOnChanges(changes: SimpleChanges): void {
    const fieldsChanged = changes['filterFields'];
    const itemsChanged = changes['items'];
    const prefixChanged = changes['filterKeyPrefix'];
    

    // Actualizar collapseKey si cambia filterKeyPrefix
    if (prefixChanged || !this.collapseKey) {
      const prefix = this.filterKeyPrefix || 'default';
      this.collapseKey = `filtersCollapsed_${prefix}`;
      const stored = localStorage.getItem(this.collapseKey);
      // this.isFiltersCollapsed = stored === 'true';
    }

    const filterFieldsReady = !!this.filterFields?.length;
    const itemsReady = !!this.items?.length;

    if (fieldsChanged) {
      this.buildForm();
    }

    // Aplicar filtros si hay campos definidos (independientemente de si hay datos)
    if (filterFieldsReady) {
      this.applyFilters('ngOnChanges');
    }
    // Si no hay campos pero sí hay items, aplicar filtros básicos
    else if (itemsChanged) {
      this.applyFilters('ngOnChanges');
    }
  }

  /**
   * Construye el formulario reactivo de filtros basándose en `filterFields`.
   * Asigna controles iniciales, configura escucha de cambios con debounce
   * para emitir eventos y aplicar filtros.
   */
  buildForm(): void {
    const group: any = {};

    this.defaultFilterValues = {}; // Reset defaults

   this.filterFields.forEach(field => {
  const fullKey = this.getFullKey(field.key);

  if (field.type === 'date-range') {
    group[fullKey] = this.fb.control(field.defaultValue ?? null);
    this.defaultFilterValues[fullKey] = field.defaultValue ?? null;

  } else if (field.type === 'range-inputs') {
    const defaultValue = field.defaultValue ?? { from: null, to: null };
    group[fullKey] = this.fb.group(defaultValue);
    this.defaultFilterValues[fullKey] = defaultValue;

  } else if (field.type === 'range-slider') {
    const defaultValue = field.defaultValue ?? [field.min ?? 0, field.max ?? 5000];
    group[fullKey] = this.fb.control(defaultValue);
    this.defaultFilterValues[fullKey] = defaultValue;

    this.field = field;

    group[fullKey].valueChanges.subscribe(([min, max]: [number, number]) => {
      this.rangeValue = [min, max];
      this.updateThumbPositions(min, max);
    });

    this.rangeValue = [defaultValue[0], defaultValue[1]];
    this.updateThumbPositions(defaultValue[0], defaultValue[1]);

  } else {
    group[fullKey] = this.fb.control(field.defaultValue ?? '');
    this.defaultFilterValues[fullKey] = field.defaultValue ?? '';
  }
});


    this.filterForm = this.fb.group(group);

    // Aplica filtros iniciales justo tras construir el form
    this.applyFilters('buildForm');

    // Suscripción a cambios del formulario con debounce para filtrar en vivo
    this.filterForm.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(values => {
        const hasIncompleteDateRange = this.filterFields.some(field => {
          const fullKey = this.getFullKey(field.key);
          return field.type === 'date-range' &&
            (!!values[fullKey]?.from !== !!values[fullKey]?.to);
        });

        if (hasIncompleteDateRange) return; // Evitar filtrar con rango de fechas incompleto

        if (JSON.stringify(this.lastEmittedFilters) !== JSON.stringify(values)) {
          this.lastEmittedFilters = values;
          this.currentPage = 1;
          this.filtersChanged.emit(values);
          this.applyFilters('valueChanges');
        }
      });
  }

  /**
   * Aplica los filtros definidos sobre `items` y actualiza la lista
   * filtrada y paginada. Emite eventos con la cantidad filtrada.
   *
   * @param fromfunction Opcional. Fuente que invoca la función (para debugging).
   */
  applyFilters(fromfunction: string = ''): void {
    const values = this.filterForm?.value || {};

    // Si no hay items, establecer array vacío
    if (!this.items?.length) {
      this.filteredItems = [];
      this.currentPage = 1;
      this.updatePaginatedItems();
      this.filteredCount.emit(0);
      this.filteredItemsChange.emit([]);
      return;
    }

    // Si no hay filtros o valores, mostrar todos
    if (!this.filterFields?.length || Object.keys(values).length === 0) {
      this.filteredItems = [...this.items];
    } else {
      this.filteredItems = this.items.filter(item => {
        return this.filterFields.every(field => {
          const fullKey = this.getFullKey(field.key);
          const value = values[fullKey];
          
          if (field.customFilter) {
            return field.customFilter(item, value);
          }
          
          const itemValue = field.key.split('.').reduce((acc, part) => acc?.[part], item); //item[field.key];

          switch (field.type) {
            case 'text':
              if (!value) return true;

              const valueStr = String(value).toLowerCase();
              const itemValStr = String(itemValue ?? '').toLowerCase();

              // Si hay searchKeys, buscar en campos anidados
              if (field.searchKeys?.length) {
                return field.searchKeys.some(path => {
                  const [section, key] = path.split('.');
                  const fieldArray = item[section];
                  if (Array.isArray(fieldArray)) {
                    const match = fieldArray.find((f: any) => f.name === key);
                    return match?.value?.toLowerCase().includes(value.toLowerCase());
                  } else {
                    return fieldArray?.toLowerCase().includes(value.toLowerCase());
                  }
                  return false;
                });
              }

              return itemValStr.includes(valueStr);

            case 'select':
              if (value === null || value === undefined || value === '' || value === -1) return true;
              return String(itemValue) === String(value);

            case 'checkbox':
              return !value || !!itemValue === true;

            case 'range-slider':
              const [min_slider, max_slider] = value || [];
              const numericValue_slider: number = +itemValue;

              if (min_slider !== null && numericValue_slider < min_slider) return false;
              if (max_slider !== null && numericValue_slider > max_slider) return false;

              return true;

            case 'range-inputs':
              const min: number | null = value?.from ?? null;
              const max: number | null = value?.to ?? null;
              const numericValue: number = +itemValue;

              if (min !== null && numericValue < min) return false;
              if (max !== null && numericValue > max) return false;

              return true;



            case 'date-range':
              const from: Date | null = value?.from?.toLocalNativeDate?.() ?? null;
              let to: Date | null = value?.to?.toLocalNativeDate?.() ?? null;

              // Ajustar 'to' para incluir todo el día (23:59:59.999)
              if (to) {
                to = new Date(to);
                to.setHours(23, 59, 59, 999);
              }
              const itemDate: Date | null = itemValue ? new Date(itemValue) : null;

              if (from && to && itemDate instanceof Date && !isNaN(itemDate.getTime())) {
                return itemDate >= from && itemDate <= to;
              }

              return true;

            default:
              return true;
          }
        });
      });
    }

    this.filteredCount.emit(this.filteredItems.length);
    this.filteredItemsChange.emit(this.filteredItems);
    this.activeFiltersChange.emit(values);
    this.updatePaginatedItems();
  }

  /**
   * Resetea los filtros a valores iniciales y emite evento vacío.
   */
  resetFilters(): void {
    this.filterForm.reset(this.defaultFilterValues);
    this.filtersChanged.emit(this.defaultFilterValues);
    this.applyFilters('resetFilters');
  }

  /**
   * Obtiene el control del formulario para la clave dada (con prefijo aplicado).
   *
   * @param key Clave del filtro
   * @returns Control del formulario
   */
  getControl(key: string): FormControl {
    return this.filterForm.get(this.getFullKey(key)) as FormControl;
  }

  /**
   * Obtiene el control del formulario para la clave anidad dada (con prefijo aplicado).
   *
   * @param key Clave del filtro
   * @param subkey Subclave del filtro
   * @returns Control del formulario
   */
  getNestedControl(fieldKey: string, subKey: string): FormControl | null {
    console.log(fieldKey, subKey)
    const group = this.getControl(fieldKey);
    console.log(group)
    if (group instanceof FormGroup) {
      const control = group.get(subKey);
      return control instanceof FormControl ? control : null;
    }
    return null;
  }


  /**
   * Actualiza la lista de items paginados según la página actual y tamaño de página.
   */
  updatePaginatedItems(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    this.paginatedItems = this.filteredItems.slice(start, start + this.pageSize);
  }

  /**
   * Navega a la página siguiente, si existe, y emite evento.
   */
  nextPage(): void {
    if ((this.currentPage * this.pageSize) < this.filteredItems.length) {
      this.currentPage++;
      this.pageChanged.emit(this.currentPage);
      this.updatePaginatedItems();
    }
  }

  /**
   * Navega a la página anterior, si existe, y emite evento.
   */
  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.pageChanged.emit(this.currentPage);
      this.updatePaginatedItems();
    }
  }

  /**
   * Maneja el evento de clic sobre un item y emite el item clicado.
   *
   * @param item Item clicado
   */
  handleItemClick(item: any): void {
    this.itemClick.emit(item);
  }

  /**
   * Construye la clave completa del filtro, agregando prefijo si existe.
   *
   * @param key Clave base del filtro
   * @returns Clave completa con prefijo
   */
  private getFullKey(key: string): string {
    const normalizedKey = key.replace(/\./g, '_');
    return this.filterKeyPrefix ? `${this.filterKeyPrefix}_${normalizedKey}` : normalizedKey;
  }

  updateThumbPositions(min: number, max: number): void {
    const rangeMin = this.field?.min ?? 0;
    const rangeMax = this.field?.max ?? 5000;
    const total = rangeMax - rangeMin;

    if (total > 0) {
      this.leftThumbPosition = ((min - rangeMin) / total) * 100;
      this.rightThumbPosition = ((max - rangeMin) / total) * 100;
    } else {
      this.leftThumbPosition = 0;
      this.rightThumbPosition = 100;
    }
  }

  downloadCSV(data: any[], filename: string = (this.filterKeyPrefix ? this.filterKeyPrefix : 'resultados')+'.csv'): void {
    if (!data || !data.length) {
      return;
    }

    const separator = ',';
    const keys = Object.keys(data[0]);

    const csvContent =
      keys.join(separator) + '\n' +
      data.map(row => {
        return keys.map(k => {
          let cell = row[k] === null || row[k] === undefined ? '' : row[k];
          cell = typeof cell === 'string' ? `"${cell.replace(/"/g, '""')}"` : cell;
          return cell;
        }).join(separator);
      }).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

}
